﻿import { useMemo, useState } from "react";

interface ClienteBase {
  id: string;
  nombre: string;
  rut: string;
  direccion: string;
  sector: string;
  customerType?: "domiciliario" | "empresa" | "institucion";
}

type ContractStatus = "vigente" | "suspendido" | "cortado";

type TariffSelectOption = {
  id: string;
  code: string;
  name: string;
};

type SectorSelectOption = {
  id: string;
  code: string;
  name: string;
};

export interface NewContractPayload {
  address: string;
  startDate: string;
  sectorId: string;
  tariffId: string;
  meterType: string;
  meterNumber: string;
  sealNumber: string;
  status: ContractStatus;
  billingDocumentType: "39" | "41" | "33" | "34";
  billingRecipientName: string;
}

interface Props {
  cliente: ClienteBase;
  tariffOptions?: TariffSelectOption[];
  sectorOptions?: SectorSelectOption[];
  onClose: () => void;
  onCreated?: (payload: NewContractPayload) => void | Promise<void>;
  mode?: "create" | "edit";
  initialValues?: Partial<NewContractPayload>;
}

export default function NuevoContratoModal({ cliente, tariffOptions = [], sectorOptions = [], onClose, onCreated, mode = "create", initialValues }: Props) {
  const defaultTariffId = tariffOptions[0]?.id ?? "";
  const defaultSectorId = sectorOptions[0]?.id ?? "";
  const [form, setForm] = useState<NewContractPayload>({
    address: initialValues?.address ?? cliente.direccion,
    startDate: initialValues?.startDate ?? new Date().toISOString().slice(0, 10),
    sectorId: initialValues?.sectorId ?? defaultSectorId,
    tariffId: initialValues?.tariffId ?? defaultTariffId,
    meterType: initialValues?.meterType ?? "Mecanico",
    meterNumber: initialValues?.meterNumber ?? "",
    sealNumber: initialValues?.sealNumber ?? "",
    status: initialValues?.status ?? "vigente",
    billingDocumentType: initialValues?.billingDocumentType ?? "39",
    billingRecipientName: initialValues?.billingRecipientName ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customerTypeLabel = useMemo(() => {
    if (cliente.customerType === "empresa") return "Empresa";
    if (cliente.customerType === "institucion") return "Institucion";
    return "Domiciliario";
  }, [cliente.customerType]);

  function set<K extends keyof NewContractPayload>(key: K, value: NewContractPayload[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError(null);
  }

  function validate(): boolean {
    if (
      !form.address.trim() ||
      !form.startDate ||
      !form.sectorId ||
      !form.tariffId ||
      !form.meterType.trim() ||
      !form.meterNumber.trim() ||
      !form.sealNumber.trim()
    ) {
      setError("Completa todos los campos obligatorios del contrato");
      return false;
    }
    if (sectorOptions.length === 0 || tariffOptions.length === 0) {
      setError("No hay sectores o tarifas configuradas");
      return false;
    }
    return true;
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onCreated?.(form);
      setSaving(false);
      onClose();
    } catch {
      setSaving(false);
      setError("No se pudo guardar el contrato");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600">
          <div>
            <h2 className="font-semibold text-white">{mode === "edit" ? "Editar Contrato" : "Nuevo Contrato"}</h2>
            <p className="text-teal-100 text-xs">{cliente.nombre} | Tipo cliente: {customerTypeLabel}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Direccion *</label>
            <input value={form.address} onChange={(e) => set("address", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Razon receptor</label>
            <input
              value={form.billingRecipientName}
              onChange={(e) => set("billingRecipientName", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              placeholder="Opcional. Si queda vacio, se usa el nombre del cliente"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha alta *</label>
              <input type="date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sector *</label>
              <select value={form.sectorId} onChange={(e) => set("sectorId", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                {sectorOptions.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.code ? `${sector.code} - ` : ""}{sector.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tarifa *</label>
              <select value={form.tariffId} onChange={(e) => set("tariffId", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                {tariffOptions.map((tariff) => (
                  <option key={tariff.id} value={tariff.id}>
                    {tariff.code ? `${tariff.code} - ` : ""}{tariff.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo medidor *</label>
              <select value={form.meterType} onChange={(e) => set("meterType", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="Mecanico">Mecanico</option>
                <option value="Digital">Digital</option>
                <option value="Inteligente">Inteligente</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Estado *</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value as ContractStatus)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="vigente">Vigente</option>
                <option value="suspendido">Suspendido</option>
                <option value="cortado">Cortado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo documento DTE *</label>
              <select
                value={form.billingDocumentType}
                onChange={(e) => set("billingDocumentType", e.target.value as NewContractPayload["billingDocumentType"])}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="39">39 - Boleta afecta</option>
                <option value="41">41 - Boleta exenta</option>
                <option value="33">33 - Factura afecta</option>
                <option value="34">34 - Factura exenta</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">N° medidor *</label>
              <input value={form.meterNumber} onChange={(e) => set("meterNumber", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="MED-0001" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">N° sello *</label>
              <input value={form.sealNumber} onChange={(e) => set("sealNumber", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="SEL-0001" />
            </div>
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700 rounded-lg">{error}</div>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-70">
              {saving ? (mode === "edit" ? "Guardando..." : "Creando...") : (mode === "edit" ? "Guardar cambios" : "Crear Contrato")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
